import React from 'react';
import { ToggleButton } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { useL10n } from '@uplift/hooks';
import { AppointmentSlotDuration, SlotStepByMedicalRole } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { AppointmentDurationMappingDurationMinutes } from '../../../../Calendar';
import { MobileDropdown } from '../../../../Inputs';
import { ProviderMedicalRole } from '@uplift/types';
import { DateFormats } from '@uplift/constants';


export const PurpleToggleButton = withStyles(ToggleButton, (theme, _params, classes) => ({
  root: {
    borderColor: theme.palette.grey[300],
    borderRadius: 0,
    flex: 1,

    '&.Mui-disabled': {
      opacity: 0.6,
    },

    [`&.${classes.selected}`]: {
      borderColor: theme.palette.secondary.main,
      background: theme.palette.secondary.main,

      '&:hover': {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.common.white,
      }
    },
  },
  label: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    textTransform: 'none',
  },
  selected: {
    color: theme.palette.common.white,
  }
}));


export type SlotDurationInputProps = {
  name: string;
  label?: string;
  startTime: string | null;
  slots: Set<string>;
  medicalRole: ProviderMedicalRole;
};


export const SlotDurationInput: React.FC<SlotDurationInputProps> = ({ name, label, startTime, slots, medicalRole, ...props }) => {
  const l10n = useL10n();

  const availableDurations = React.useMemo(() => {
    const start = CalendarDateService.date(startTime).utc();

    if (!start.isValid() || !startTime) {
      return {};
    }

    const slotMinutesDuration = SlotStepByMedicalRole[medicalRole ?? ProviderMedicalRole.therapist];
    const availableDurations: Partial<Record<AppointmentSlotDuration, boolean>> = {};
    const allDurations = {
      [ProviderMedicalRole.therapist]: [
        AppointmentSlotDuration.fifteen,
        AppointmentSlotDuration.thirty,
        AppointmentSlotDuration.forty_five,
        AppointmentSlotDuration.sixty,
      ],
      [ProviderMedicalRole.psychiatrist]: [
        AppointmentSlotDuration.fifteen,
        AppointmentSlotDuration.twenty,
        AppointmentSlotDuration.thirty,
        AppointmentSlotDuration.forty_five,
        AppointmentSlotDuration.sixty,
      ],
    }[medicalRole];

    for (const duration of allDurations) {
      const durationInMinutes = AppointmentDurationMappingDurationMinutes[duration];
      let isDurationAvailable = false;
      const stepsCount = durationInMinutes / slotMinutesDuration;

      for (let i = 0; i < stepsCount; i++) {
        const durationMinutes = (i + 1) * slotMinutesDuration;
        const c = start.add(durationInMinutes - durationMinutes, 'minutes').format(DateFormats.ISODayjs);
        const available = slots.has(c);

        if (available) {
          isDurationAvailable = true;
        } else {
          isDurationAvailable = false;
          break;
        }
      }

      if (isDurationAvailable) {
        availableDurations[duration] = true;
      } else {
        break;
      }
    }

    return availableDurations;
  }, [ medicalRole, startTime, slots ]);

  const options = React.useMemo(() => {
    const slots = [];
    const duration = `${l10n.time.min} ${l10n.sessions.session.toLowerCase()}`;

    if (medicalRole === ProviderMedicalRole.psychiatrist && availableDurations.fifteen) {
      slots.push({ value: AppointmentSlotDuration.fifteen, label: `15 ${duration}` });
    }

    if (medicalRole === ProviderMedicalRole.psychiatrist && availableDurations.twenty) {
      slots.push({ value: AppointmentSlotDuration.twenty, label: `20 ${duration}` });
    }

    if (availableDurations.thirty) {
      slots.push({ value: AppointmentSlotDuration.thirty, label: `30 ${duration}` });
    }

    if (availableDurations.forty_five) {
      slots.push({ value: AppointmentSlotDuration.forty_five, label: `45 ${duration}` });
    }

    if (availableDurations.sixty) {
      slots.push({ value: AppointmentSlotDuration.sixty, label: `60 ${duration}` });
    }

    return slots;
  }, [ availableDurations, l10n.time.min, l10n.sessions.session, medicalRole ]);

  return (
    <MobileDropdown name={name} label={label} items={options} {...props} />
  );
};
